import { useState, useEffect, useCallback, useMemo } from "react";
import { RefreshCw, Loader2, Users, MapPin, Info } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_BREAKDOWN_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, EmptyState, StatTile, money, num, pct, resolveDateRange,
    StatusFilterSelect,
} from "./adReportKit";

/**
 * One component serving the Audience and Geographic reports.
 *
 * They ask the same question — "where is the money going and what does it cost
 * there" — of different dimensions, so they share a query and a layout and
 * differ only in which breakdowns they offer.
 */
const MODES = {
    audience: {
        title: "Ad Audience",
        icon: Users,
        blurb: "Which age, placement and device your budget reaches — and what a lead costs in each.",
        tabs: [
            { key: "age_gender", label: "Age & Gender" },
            { key: "platform", label: "Placement" },
            { key: "device", label: "Device" },
        ],
    },
    geography: {
        title: "Ad Geography",
        icon: MapPin,
        blurb: "Where your spend lands by region, and where a lead is cheapest.",
        tabs: [{ key: "region", label: "Region" }],
    },
};

/** Heatmap-style cell tint: cheaper than blended is green, dearer is red. */
const cplTone = (vsBlended) => {
    if (vsBlended == null) return "bg-muted/40 text-muted-foreground";
    if (vsBlended <= -25) return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300";
    if (vsBlended <= -5) return "bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400";
    if (vsBlended < 25) return "bg-muted/50 text-foreground";
    if (vsBlended < 75) return "bg-amber-100 dark:bg-amber-900/25 text-amber-800 dark:text-amber-300";
    return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
};

/** Age × gender laid out as an actual matrix rather than a flat list. */
const AgeGenderMatrix = ({ rows }) => {
    const { ages, genders, cells } = useMemo(() => {
        const a = [...new Set(rows.map((r) => r.dim1))].sort();
        const g = [...new Set(rows.map((r) => r.dim2))].sort();
        const map = {};
        rows.forEach((r) => { map[`${r.dim1}|${r.dim2}`] = r; });
        return { ages: a, genders: g, cells: map };
    }, [rows]);

    if (!ages.length) return null;

    return (
        <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
                <thead className="bg-muted/50">
                    <tr className="text-muted-foreground">
                        <th className="px-3 py-2.5 text-left font-medium">Age</th>
                        {genders.map((g) => (
                            <th key={g} className="px-3 py-2.5 text-center font-medium capitalize">{g}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {ages.map((age) => (
                        <tr key={age}>
                            <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{age}</td>
                            {genders.map((g) => {
                                const c = cells[`${age}|${g}`];
                                if (!c) return <td key={g} className="px-2 py-2 text-center text-muted-foreground">—</td>;
                                return (
                                    <td key={g} className="px-2 py-1.5">
                                        <div className={cn("rounded-md px-2 py-1.5 text-center", cplTone(c.vs_blended_pct))}
                                            title={`Spend ${money(c.spend)} · ${c.leads} leads · CPL ${money(c.cpl)}`}>
                                            <div className="font-semibold tabular-nums">{money(c.cpl)}</div>
                                            <div className="text-[9px] opacity-80 tabular-nums">
                                                {money(c.spend)} · {c.leads} leads
                                            </div>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default function AdBreakdownReport({ mode = "audience" }) {
    const { fetch } = useApiMethods();
    const cfg = MODES[mode] || MODES.audience;
    const Icon = cfg.icon;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [breakdown, setBreakdown] = usePersistedState(`ad_bd_${mode}_tab`, cfg.tabs[0].key);

    const [datePreset, setDatePreset] = usePersistedState(`ad_bd_${mode}_preset`, "last_month");
    const [customFrom, setCustomFrom] = usePersistedState(`ad_bd_${mode}_from`, "");
    const [customTo, setCustomTo] = usePersistedState(`ad_bd_${mode}_to`, "");
    const [accountId, setAccountId] = usePersistedState(`ad_bd_${mode}_account`, "");
    const [statusFilter, setStatusFilter] = usePersistedState(`ad_bd_${mode}_status`, "");

    // Guards against a persisted tab from the other mode leaking in.
    const activeTab = cfg.tabs.some((t) => t.key === breakdown) ? breakdown : cfg.tabs[0].key;

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams({ breakdown: activeTab });
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);

        fetch(`${AD_BREAKDOWN_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [activeTab, datePreset, customFrom, customTo, accountId, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const rows = data?.data || [];
    const s = data?.summary;
    const best = rows.filter((r) => r.cpl != null).sort((a, b) => a.cpl - b.cpl)[0];
    const worst = rows.filter((r) => r.cpl != null).sort((a, b) => b.cpl - a.cpl)[0];

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Icon className="h-6 w-6 text-primary" />
                            {cfg.title}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">{cfg.blurb}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                {cfg.tabs.length > 1 && (
                    <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
                        {cfg.tabs.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setBreakdown(t.key)}
                                className={cn(
                                    "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                    activeTab === t.key
                                        ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                        : "text-muted-foreground hover:text-foreground",
                                )}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                    >
                        <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                </AdFilterBar>

                {data?.coverage_from && (
                    <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] bg-muted/50 text-muted-foreground">
                        <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                        <span>
                            Breakdown data is stored from <strong className="text-foreground">{data.coverage_from}</strong> onward.
                            A breakdown multiplies rows by the number of cells, so it is synced for a
                            trailing window rather than the full history — an earlier date range will look
                            empty because it was never pulled, not because nothing was spent.
                        </span>
                    </div>
                )}

                {s && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile tone="spend" label="Spend" value={money(s.spend)} sub={`${s.cells} segments`} />
                        <StatTile tone="leads" label="Leads" value={num(s.leads)} />
                        <StatTile tone="cost" label="Blended CPL" value={money(s.cpl)} />
                        <StatTile
                            label="Cheapest segment"
                            value={best ? money(best.cpl) : "—"}
                            sub={best ? best.label : null} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && rows.length === 0 && (
                    <EmptyState
                        title="No breakdown data for this period"
                        hint="Run a sync from Settings → Meta Ads. Breakdowns are pulled for a trailing window, so very old ranges will be empty." />
                )}

                {!loading && rows.length > 0 && (
                    <>
                        {activeTab === "age_gender" && <AgeGenderMatrix rows={rows} />}

                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium">Segment</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Spend</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Share</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Impressions</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Clicks</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CTR</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Leads</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CPL</th>
                                        <th className="px-3 py-2.5 text-right font-medium">vs blended</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rows.map((r) => (
                                        <tr key={`${r.dim1}|${r.dim2}`} className="hover:bg-accent/30">
                                            <td className="px-3 py-2 text-foreground capitalize">
                                                {r.label.replace(/_/g, " ")}
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium text-foreground tabular-nums">{money(r.spend)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="h-1 w-8 rounded-full bg-muted overflow-hidden inline-block align-middle">
                                                        <span className="block h-full bg-blue-500"
                                                            style={{ width: `${Math.min(r.spend_share || 0, 100)}%` }} />
                                                    </span>
                                                    {pct(r.spend_share, 1)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(r.impressions)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(r.clicks)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{pct(r.ctr)}</td>
                                            <td className="px-3 py-2 text-right text-foreground font-medium tabular-nums">{num(r.leads)}</td>
                                            <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-400 tabular-nums">{money(r.cpl)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {r.vs_blended_pct == null ? (
                                                    <span className="text-muted-foreground">—</span>
                                                ) : (
                                                    <span className={cn("font-medium",
                                                        r.vs_blended_pct < 0
                                                            ? "text-emerald-600 dark:text-emerald-400"
                                                            : "text-red-600 dark:text-red-400")}>
                                                        {r.vs_blended_pct > 0 ? "+" : ""}{r.vs_blended_pct}%
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {best && worst && best !== worst && (
                            <p className="text-[11px] text-muted-foreground">
                                Cheapest segment is <strong className="text-foreground">{best.label.replace(/_/g, " ")}</strong> at {money(best.cpl)};
                                dearest is <strong className="text-foreground">{worst.label.replace(/_/g, " ")}</strong> at {money(worst.cpl)}
                                {best.cpl > 0 && ` — a ${(worst.cpl / best.cpl).toFixed(1)}× difference`}.
                                Segments with no leads show a dash rather than a zero cost.
                            </p>
                        )}
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
